import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, FileText, Search, Phone, MapPin, Headphones, Globe, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const SESSION_KEY = "mica_session";

function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

const GREETING =
  "👋 Hello, I'm **MICA**, your AI Claim Assistant.\n\nNeed help filing a motor insurance claim, finding a cashless garage, tracking a claim, or understanding claim documents? I'm here 24×7.";

const QUICK_ACTIONS = [
  { key: "file",      label: "File a Claim",         icon: FileText,   to: "/file-claim",       prompt: "I want to file a new motor insurance claim." },
  { key: "track",     label: "Track My Claim",       icon: Search,     to: "/track",            prompt: "How do I track my claim status?" },
  { key: "emergency", label: "Emergency Assistance", icon: Phone,      to: "/emergency",        prompt: "I just had an accident and I need emergency assistance." },
  { key: "garage",    label: "Find Cashless Garage", icon: MapPin,     to: "/emergency?type=find_garage", prompt: "Help me find a nearby cashless garage." },
  { key: "expert",    label: "Talk to Claim Expert", icon: Headphones, to: "/emergency?type=call_back",   prompt: "I'd like to talk to a human claim expert." },
];

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "bn", label: "বাংলা" },
  { code: "mr", label: "मराठी" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
];

const LANG_BCP47 = {
  en: "en-IN", hi: "hi-IN", bn: "bn-IN", mr: "mr-IN", ta: "ta-IN",
  te: "te-IN", kn: "kn-IN", ml: "ml-IN", gu: "gu-IN", pa: "pa-IN",
};

function stripMarkdown(text) {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/[*_`#>~-]/g, " ")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function renderMarkdownLite(text) {
  // tiny markdown: bold **x** and bullets - / •
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="font-bold">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function MICAAssistant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [lang, setLang] = useState("en");
  const [showLang, setShowLang] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem("mica_voice") === "1";
  });
  const [voiceSupported, setVoiceSupported] = useState(false);
  const endRef = useRef(null);
  const recogRef = useRef(null);
  const lastSpokenRef = useRef("");

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    const synth = typeof window !== "undefined" && window.speechSynthesis;
    setVoiceSupported(Boolean(SR && synth));
  }, []);

  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("mica_voice", voiceEnabled ? "1" : "0");
    }
    if (!voiceEnabled && typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, [voiceEnabled]);

  // Speak the last assistant message whenever a stream completes
  useEffect(() => {
    if (!voiceEnabled || streaming || !voiceSupported) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || !last.content) return;
    if (lastSpokenRef.current === last.content) return;
    lastSpokenRef.current = last.content;
    speak(last.content);
  }, [streaming, voiceEnabled, voiceSupported, messages.length]);

  const speak = (text) => {
    if (!voiceEnabled || !window.speechSynthesis) return;
    const clean = stripMarkdown(text);
    if (!clean) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(clean.slice(0, 800));
    utter.lang = LANG_BCP47[lang] || "en-IN";
    utter.rate = 1.0;
    utter.pitch = 1.0;
    utter.onstart = () => setSpeaking(true);
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utter);
  };

  const stopSpeaking = () => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  const startListening = () => {
    if (!voiceSupported || listening) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recog = new SR();
    recog.lang = LANG_BCP47[lang] || "en-IN";
    recog.continuous = false;
    recog.interimResults = true;
    recog.maxAlternatives = 1;
    recog.onstart = () => { stopSpeaking(); setListening(true); };
    recog.onerror = (e) => { setListening(false); if (e.error !== "aborted") console.warn("SR error:", e.error); };
    recog.onend = () => setListening(false);
    let finalTranscript = "";
    recog.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += t;
        else interim += t;
      }
      setInput(finalTranscript + interim);
      if (finalTranscript) {
        recog.stop();
        setTimeout(() => send(finalTranscript.trim()), 100);
      }
    };
    recogRef.current = recog;
    recog.start();
  };

  const stopListening = () => {
    recogRef.current?.stop();
    setListening(false);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const csrfMatch = typeof document !== "undefined" && document.cookie.match(/(?:^|; )csrf_token=([^;]+)/);
      const csrf = csrfMatch ? decodeURIComponent(csrfMatch[1]) : "";
      const headers = { "Content-Type": "application/json" };
      if (csrf) headers["X-CSRF-Token"] = csrf;
      const langPrefix = lang !== "en"
        ? `[Reply in ${LANGS.find((l) => l.code === lang)?.label || lang}.] `
        : "";
      const resp = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ session_id: getSessionId(), message: langPrefix + msg }),
      });
      if (!resp.body) throw new Error("No stream");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again in a moment." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const handleQuickAction = (action) => {
    // route immediately if the destination exists, otherwise prompt MICA.
    if (action.to) navigate(action.to);
    setOpen(false);
  };

  return (
    <>
      {/* Floating launcher – shifted up & left of bottom-right corner to avoid platform badge collision */}
      <button
        onClick={() => setOpen((s) => !s)}
        className="fixed bottom-24 right-6 md:bottom-10 md:right-10 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-[#003DA5] to-[#00A86B] text-white shadow-2xl flex items-center justify-center animate-pulse-ring transition-transform hover:scale-105"
        aria-label="MICA AI Assistant"
        data-testid="mica-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
      </button>
      {!open && (
        <div className="fixed bottom-40 right-6 md:bottom-28 md:right-10 z-[59] bg-white rounded-full px-3 py-1.5 shadow-md border border-slate-200 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 hidden sm:block animate-float">
          Ask MICA · 24×7
        </div>
      )}

      {open && (
        <div
          className="fixed bottom-40 right-6 md:bottom-28 md:right-10 z-[60] w-[92vw] max-w-[420px] h-[640px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden fade-in-up"
          data-testid="mica-panel"
        >
          {/* Header */}
          <div className="p-4 bg-gradient-to-r from-[#003DA5] via-[#0A5BBD] to-[#00A86B] text-white">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#00A86B] border-2 border-white" />
                </div>
                <div>
                  <div className="font-heading font-extrabold text-lg leading-tight">MICA</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-85 leading-tight">AI Claim Expert · 24×7</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {voiceSupported && (
                  <button
                    onClick={() => setVoiceEnabled((v) => !v)}
                    className={`flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${voiceEnabled ? "bg-white text-[#003DA5]" : "bg-white/15 hover:bg-white/25 text-white"}`}
                    data-testid="mica-voice-toggle"
                    title={voiceEnabled ? "Voice replies ON" : "Voice replies OFF"}
                  >
                    {speaking ? <Volume2 className="w-3.5 h-3.5 animate-pulse" /> : voiceEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                  </button>
                )}
                <div className="relative">
                  <button
                    onClick={() => setShowLang((s) => !s)}
                    className="flex items-center gap-1 text-xs font-semibold bg-white/15 hover:bg-white/25 rounded-full px-2.5 py-1"
                    data-testid="mica-lang-toggle"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    {LANGS.find((l) => l.code === lang)?.label || "EN"}
                  </button>
                  {showLang && (
                    <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-xl shadow-lg border border-slate-200 max-h-64 overflow-y-auto z-10" data-testid="mica-lang-menu">
                      {LANGS.map((l) => (
                        <button
                          key={l.code}
                          onClick={() => { setLang(l.code); setShowLang(false); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${lang === l.code ? "font-bold text-[#003DA5]" : "text-slate-700"}`}
                          data-testid={`mica-lang-${l.code}`}
                        >
                          {l.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#003DA5] text-white rounded-br-sm"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                  }`}
                  data-testid={`mica-msg-${m.role}`}
                >
                  {m.content ? renderMarkdownLite(m.content) : (streaming && i === messages.length - 1 ? <span className="inline-block w-2 h-4 bg-slate-400 animate-pulse" /> : "")}
                </div>
              </div>
            ))}

            {messages.length === 1 && (
              <div className="space-y-2 pt-2" data-testid="mica-quick-actions">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Quick actions</div>
                {QUICK_ACTIONS.map((a) => {
                  const Icon = a.icon;
                  return (
                    <button
                      key={a.key}
                      onClick={() => handleQuickAction(a)}
                      className="w-full flex items-center gap-3 bg-white border border-slate-200 hover:border-[#003DA5] hover:bg-[#E6F0FF]/50 rounded-xl px-3 py-2.5 text-left transition-colors"
                      data-testid={`mica-action-${a.key}`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#E6F0FF] text-[#003DA5] flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-bold text-slate-900">{a.label}</div>
                      </div>
                    </button>
                  );
                })}
                <div className="text-[10px] text-center text-slate-400 pt-2">— or just type your question below —</div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-slate-200 bg-white flex items-center gap-2"
          >
            {voiceSupported && (
              <button
                type="button"
                onClick={() => (listening ? stopListening() : startListening())}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shrink-0 ${
                  listening
                    ? "bg-rose-500 text-white animate-pulse-ring"
                    : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                }`}
                data-testid="mica-mic"
                title={listening ? "Stop listening" : `Speak in ${LANGS.find(l => l.code === lang)?.label || "English"}`}
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={listening ? "Listening…" : "Ask MICA anything…"}
              className="flex-1 px-3 py-2.5 rounded-full bg-slate-100 text-sm text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#003DA5] outline-none transition-all"
              disabled={streaming}
              data-testid="mica-input"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-[#003DA5] to-[#00A86B] hover:opacity-90 disabled:from-slate-300 disabled:to-slate-300 text-white flex items-center justify-center transition-colors"
              data-testid="mica-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
